import _ from "lodash";
import React from "react";
import ACLStore from "lib/aclStore";
import { store } from "store/createStore";

import {
  ImportOutlined,
  ListDashedSlantedOutlined,
  LeLibraryOutlined,
  JournalOutlined,
  SubsectionListOutlined,
  QuickTaskOutlined,
  LeOutlined,
  FaOutlined,
  SaOutlined,
  AssessmentAddOutlined,
  WorksheetClipboardOutlined,
  LinkOutlined,
  NoteOutlined,
  UploadOutlined,
  GoogleDriveColored,
  OneDriveColored,
  DeviceDesktopOutlined,
  PencilLineOutlined,
  SwitchArrowsOutlined,
  DeleteOutlined,
  DownloadOutlined,
  TagOutlined,
  StudentEvaluateOutlined,
  MinusCircleOutlined,
  MarkAsReadOutlined,
  TickCircleOutlined,
  UserCloseOutlined,
  TickCircleOpenOutlined,
  UserTickOutlined,
  ClassProgressOutlined,
  SectionListOutlined,
  CaretOpenedFilled,
  CaretClosedFilled,
  AiOutlined,
  DotsHorizontalOutlined,
  EyeOutlined,
} from "@toddle-edu/ds-icons";
import { Avatar } from "@toddle-edu/ds-web";
import {
  ENABLE_ASSESSMENTS_IN_PYP,
  ENABLE_AI_TUTOR,
  OPEN_AND_CREATE_ASSIGNMENTS_IN_EDIT_MODE,
  ENABLE_GRADE_WISE_ASSESSMENT,
} from "Constants/permissionConstants";
import { ASSESSMENT_TYPE_WORKSHEET } from "Worksheet/modules/WorksheetConstants";
import {
  CURRICULUM_TYPE_DP,
  CURRICULUM_TYPE_MYP,
  CURRICULUM_TYPE_PYP,
  CURRICULUM_TYPE_UBD,
  CREATE_AI_TUTOR,
  ASSESSMENT_TYPE_AI_TUTOR,
  PLAN_TYPE_PRO,
  PLAN_TYPE_PRO_PILOT,
  SUBJECT_TYPE_EE,
} from "Constants/stringConstants";
import {
  getMemoizedLangFunction,
  getMemoizedFunction,
  isUserTypeStudent,
  isUserTypeParent,
} from "Utils";
import { checkCategorySetApplicableTo } from "ScoreCategories/modules/ScoreCategoriesModule";

import { INCLUDE_ITEM_TYPES_FOR_UNREAD_MESSAGES_COUNT } from "ClassRoom/modules/ClassRoomModule/ClassRoomConstants";

import {
  PUBLISHED_STUDENT_ASSIGNMENT_STATUS,
  PUBLISHED_STUDENT_ASSIGNMENT_LOCKING_SUBMISSION_STATUS,
  ASSIGNMENT_STATE_TABS,
} from "ClassRoom/modules/ClassRoomModule/ClassRoomConstants";
import { getArchivedStudentAssignmentStatus } from "ClassRoom/modules/ClassRoomModule/ClassRoomUtils";
import { getGroupOrAssessmentTypes } from "AppComponents/Gradebook/modules/GradebookGraphqlHelpers";
import {
  getNestedChildren,
  splitAssessmentAndResourceTypesMemoized,
} from "Utils/EntityFlowUtils";
import { getClassMaterialItemFromCache } from "./ClassMaterialsGraphqlHelpers";
import { getAssessmentTemplatesFromCache } from "ChildrenPage/modules/ChildrenPageGraphqlHelpers";
import { isAITutorMarketingEnabled } from "Platform/modules/PlatformUtils";
import {
  MAX_DEPTH,
  MAX_FOLDER_DEPTH,
} from "UIComponents/NestedAccordion/constants";
import { shouldShowImportFromToddleLibrary } from "ToddleLibrary/modules/ToddleLibraryUtils";
import moment from "moment";
import { checkIsDateBefore } from "Utils/DateTimeHelpers";

import {
  getIsAssessmentTypeConfigurationEnabled,
  getIsAITutorInEEEnabled,
} from "Utils/ACLStoreUtils";
import { getTaskTypeAvatarName } from "Utils/EntityFlowUtils";
import { getIsGradingPeriodUnlocked } from "GradingSetup/modules/Utils";

const COURSE_PARENT_TYPE = "COURSE";
const CLASS_MATERIAL_TYPE = "CLASS_MATERIAL";
const PLANNER_FLOW_RESOURCE = "PLANNER_FLOW_RESOURCE";
const CLASS_MATERIAL_RESOURCE_TYPE = "FOLDER";
const CLASS_MATERIAL_ORDER_BY = "DISPLAY_SEQUENCE";
export const OUTLINE_VISIBILITY_LIMIT = 1008;

export const CLASS_MATERIALS_FILE_TYPES = [
  "IMAGE",
  "VIDEO",
  "LINK",
  "NOTE",
  "PDF",
  "ZIP",
  "SHEET",
  "SLIDES",
  "AUDIO",
];

const getDepthWiseSectionOptions = ({ depth, isFolder }) => {
  // this option is only available for sections
  // it creates a new section at same level of current section after its children
  const parallelSectionOption = {
    label: "classMaterials:add_section",
    key: "create_parallel_section",
    icon: <SectionListOutlined />,
  };

  const sectionOption = {
    label: "classMaterials:add_section",
    key: "create_section",
    icon: <SectionListOutlined />,
  };

  const subsectionOption = {
    label: "classMaterials:add_subsection",
    key: "create_subsection",
    icon: <SubsectionListOutlined />,
  };

  /* 'depth' here denotes the depth at which the new item will be createdAt
   * if 'isFolder' is true, that means the trigger element is a section
   * 'isCollapsed' is true then the folder element is 1 level above depth, otherwise same level
   */

  /**
   * Depth 1
   * Can only create sections
   */

  if (depth === 1) {
    return [sectionOption];
  }

  /**
   * Depth 2
   * Files:
   ** Can only create sections under files
   *
   * Folders:
   ** Section + subsection
   */

  if (depth === 2) {
    if (isFolder) {
      return [subsectionOption];
    }

    return [sectionOption];
  }

  /**
   * Depth above 2 till max folder depth
   * Files:
   ** Can only create sections below files
   *
   * Folders:
   ** Both parallel and subsections can be created
   */
  if (_.inRange(depth, 3, MAX_DEPTH)) {
    if (isFolder) {
      return [parallelSectionOption, subsectionOption];
    }

    return [sectionOption];
  }

  // Restrict sections above max folder depth
  if (depth > MAX_FOLDER_DEPTH && !isFolder) {
    return [];
  }

  // Can create a parallel section at max folder depth
  if (depth === MAX_DEPTH && isFolder) {
    return [parallelSectionOption];
  }

  return [];
};

const getAssignmentOptionKey = ({ key, isInlineCreationEnabled }) => {
  return isInlineCreationEnabled ? `${key}_INLINE` : key;
};

export const canCreateAssignmentInEditMode = () =>
  ACLStore.can(OPEN_AND_CREATE_ASSIGNMENTS_IN_EDIT_MODE);

const getAddDropDownOptions = ({
  t,
  curriculumType,
  depth,
  isTermsExistsForCurrentCurriculumProgram,
  isFolder,
  hideAssessments = false,
  course,
}) => {
  const isPYPAssessmentEnabled = ACLStore.can(ENABLE_ASSESSMENTS_IN_PYP);

  const canCreateAssessments = _.includes(
    [
      CURRICULUM_TYPE_DP,
      CURRICULUM_TYPE_MYP,
      ...(isPYPAssessmentEnabled ? [CURRICULUM_TYPE_PYP] : []),
    ],
    curriculumType
  );

  const canCreateWorksheets = ACLStore.can("FeatureFlag:ClassroomWorksheet");
  const canCreateAIChatTask =
    ACLStore.can(ENABLE_AI_TUTOR) || isAITutorMarketingEnabled();
  const isMapleBearLikeOrg = ACLStore.can("FeatureFlag:IsMapleBearLikeOrg");

  const isInlineCreationEnabled = !canCreateAssignmentInEditMode();
  const canImportFromToddleLibrary = shouldShowImportFromToddleLibrary({
    curriculumType,
  });

  const subjectType = _.get(course, "subjects.0.type", "");

  const createAITutorOption = {
    label: "common:ai_tutor",
    key: CREATE_AI_TUTOR,
    value: ASSESSMENT_TYPE_AI_TUTOR,
    icon: <AiOutlined />,
    disabled: !isTermsExistsForCurrentCurriculumProgram,
    disabledTooltip: t(
      "classRoom:assessment_creation_no_grading_periods_tooltip_with_label_plural",
      { label: t("common:ai_tutor") }
    ),
  };

  return [
    {
      label: "common:create",
      key: "CREATE",
      isItemGroup: true,
      options: [
        ...getDepthWiseSectionOptions({ depth, isFolder }),
        {
          label: "common:quick_task",
          key: getAssignmentOptionKey({
            key: "CREATE_QUICK_TASK",
            isInlineCreationEnabled,
          }),
          icon: <QuickTaskOutlined />,
          disabled: !isTermsExistsForCurrentCurriculumProgram,
          disabledTooltip: t(
            "classRoom:assessment_creation_no_grading_periods_tooltip_with_label_plural",
            { label: t("common:quick_task") }
          ),
        },
        hideAssessments &&
          subjectType === SUBJECT_TYPE_EE &&
          canCreateAIChatTask &&
          getIsAITutorInEEEnabled() && {
            ...createAITutorOption,
          },
        ...(hideAssessments
          ? []
          : [
              {
                label: "common:le_label_plural",
                key: getAssignmentOptionKey({
                  key: "CREATE_LE",
                  isInlineCreationEnabled,
                }),
                icon: <LeOutlined />,
                disabled: !isTermsExistsForCurrentCurriculumProgram,
                disabledTooltip: t(
                  "classRoom:assessment_creation_no_grading_periods_tooltip_with_label_plural",
                  { label: t("common:le_label_plural") }
                ),
              },
              ...(canCreateAssessments
                ? [
                    {
                      label: "common:formative_assessments",
                      key: getAssignmentOptionKey({
                        key: "CREATE_FA",
                        isInlineCreationEnabled,
                      }),
                      icon: <FaOutlined />,
                      disabled: !isTermsExistsForCurrentCurriculumProgram,
                      disabledTooltip: t(
                        "classRoom:assessment_creation_no_grading_periods_tooltip_with_label_plural",
                        { label: t("common:formative_assessments") }
                      ),
                    },
                    {
                      label: "common:summative_assessments",
                      key: getAssignmentOptionKey({
                        key: "CREATE_SA",
                        isInlineCreationEnabled,
                      }),
                      icon: <SaOutlined />,
                      disabled: !isTermsExistsForCurrentCurriculumProgram,
                      disabledTooltip: t(
                        "classRoom:assessment_creation_no_grading_periods_tooltip_with_label_plural",
                        { label: t("common:summative_assessments") }
                      ),
                    },
                  ]
                : []),
              ...(curriculumType === CURRICULUM_TYPE_UBD
                ? [
                    {
                      label: "common:assessment",
                      key: getAssignmentOptionKey({
                        key: "CREATE_ASSESSMENT",
                        isInlineCreationEnabled,
                      }),
                      icon: <AssessmentAddOutlined />,
                      disabled: !isTermsExistsForCurrentCurriculumProgram,
                      disabledTooltip: t(
                        "classRoom:assessment_creation_no_grading_periods_tooltip_with_label_plural",
                        { label: t("common:assessment") }
                      ),
                    },
                  ]
                : []),
              ...(canCreateWorksheets
                ? [
                    {
                      label: "common:worksheet_other",
                      key: "CREATE_WORKSHEET",
                      value: ASSESSMENT_TYPE_WORKSHEET,
                      icon: <WorksheetClipboardOutlined />,
                      disabled: !isTermsExistsForCurrentCurriculumProgram,
                      disabledTooltip: t(
                        "classRoom:assessment_creation_no_grading_periods_tooltip_with_label_plural",
                        { label: t("common:worksheet_other") }
                      ),
                    },
                  ]
                : []),
              ...(canCreateAIChatTask
                ? [
                    {
                      ...createAITutorOption,
                    },
                  ]
                : []),
            ]),
        {
          label: "common:note",
          key: "ADD_NOTE",
          icon: <NoteOutlined />,
        },
        {
          label: "common:link",
          key: "ADD_LINK",
          icon: <LinkOutlined />,
        },
        {
          label: "common:upload_from",
          key: "upload_menu",
          isSubMenu: true,
          showDivider: false,
          icon: <UploadOutlined />,
          options: [
            {
              key: "add_google_drive",
              label: "common:google_drive",
              icon: <GoogleDriveColored />,
            },
            {
              key: "add_one_drive",
              label: "common:one_drive",
              icon: <OneDriveColored />,
            },
            {
              key: "upload_device",
              label: "common:device",
              icon: <DeviceDesktopOutlined />,
            },
          ],
        },
        {
          key: "MORE",
          label: "common:more",
          icon: <DotsHorizontalOutlined />,
          isSubMenu: true,
          options: [],
        },
      ],
    },
    {
      label: "common:import_from",
      key: "import_menu",
      isSubMenu: true,
      icon: <ImportOutlined />,
      options: [
        ...(hideAssessments
          ? []
          : [
              {
                key: "IMPORT_UNIT_FLOW",
                label: "common:unit",
                icon: <ListDashedSlantedOutlined />,
                disabled: !isTermsExistsForCurrentCurriculumProgram,
                disabledTooltip: t(
                  "classRoom:import_from_label_no_grading_periods_tooltip",
                  {
                    label: t("common:unit"),
                  }
                ),
              },
            ]),
        {
          key: "IMPORT_LE_FROM_LE_LIBRARY",
          label: "common:learning_experience_and_assessment_library",
          icon: <LeLibraryOutlined />,
          disabled: !isTermsExistsForCurrentCurriculumProgram,
          disabledTooltip: t(
            "classRoom:import_from_label_no_grading_periods_tooltip",
            {
              label: t("common:learning_experience_and_assessment_library"),
            }
          ),
        },
        ...(isMapleBearLikeOrg || hideAssessments || !canImportFromToddleLibrary
          ? []
          : [
              {
                key: "IMPORT_FROM_TODDLE_LIBRARY",
                label: "common:toddle_library",
                icon: <JournalOutlined />,
                disabled: !isTermsExistsForCurrentCurriculumProgram,
                disabledTooltip: t(
                  "classRoom:import_from_label_no_grading_periods_tooltip",
                  {
                    label: t("common:toddle_library"),
                  }
                ),
              },
            ]),
        {
          key: "IMPORT_FROM_ANOTHER_CLASS",
          label: "classMaterials:another_class_or_academic_year",
          icon: <ClassProgressOutlined />,
          disabled: !isTermsExistsForCurrentCurriculumProgram,
          disabledTooltip: t(
            "classRoom:import_from_label_no_grading_periods_tooltip",
            {
              label: t("common:toddle_library"),
            }
          ),
        },
      ],
    },
  ];
};

export const getTemplateIdFromTaskType = (
  taskTypeId,
  gradeId,
  curriculumId,
  isGradeWiseAssessment = false
) => {
  const data = getAssessmentTemplatesFromCache(curriculumId);

  if (_.isEmpty(data)) {
    return null;
  }

  const taskTypes = _.get(data, "taskTypes", []);

  const taskType = _.find(taskTypes, { id: taskTypeId });

  if (_.isEmpty(taskType)) {
    return null;
  }

  const templates = _.get(taskType, "templates", []);

  // Filter templates by grade if grade-wise assessment is enabled and gradeId is provided
  if (isGradeWiseAssessment && gradeId) {
    const filteredTemplates = _.filter(templates, template => {
      const templateGrades = _.get(template, "grades", []);
      return _.some(templateGrades, grade => grade.id === gradeId);
    });

    if (_.isEmpty(filteredTemplates)) {
      return _.get(_.first(templates), "id", null);
    } else {
      return _.get(_.first(filteredTemplates), "id", null);
    }
  }

  // When not filtering by grade, return the first template
  return _.get(_.first(templates), "id", null);
};

const getAddDropDownOptionsV2 = ({
  t,
  curriculumType,
  curriculumProgramId,
  depth,
  isTermsExistsForCurrentCurriculumProgram,
  isFolder,
  hideAssessments = false,
  gradeId,
}) => {
  const isMapleBearLikeOrg = ACLStore.can("FeatureFlag:IsMapleBearLikeOrg");
  const isInlineCreationEnabled = !canCreateAssignmentInEditMode();
  const canImportFromToddleLibrary = shouldShowImportFromToddleLibrary({
    curriculumType,
  });

  const assessmentTemplatesData = getAssessmentTemplatesFromCache(
    curriculumProgramId
  );
  const taskTypes = _.get(assessmentTemplatesData, "taskTypes", []);

  const sortedTaskTypes = _.sortBy(taskTypes, "displaySequence");

  const isGradeWiseAssessment = ACLStore.can(ENABLE_GRADE_WISE_ASSESSMENT);
  const isAssessmentTypeConfigurationEnabled = getIsAssessmentTypeConfigurationEnabled();

  const assessmentOptions = _.map(sortedTaskTypes, taskType => {
    const type = _.get(taskType, "type", "");
    const label = _.get(taskType, "label", "");
    const color = _.get(taskType, "color", "");
    const taskTypeId = _.get(taskType, "id", "");

    const templateId = getTemplateIdFromTaskType(
      taskTypeId,
      gradeId,
      curriculumProgramId,
      isGradeWiseAssessment
    );

    const avatarName = getTaskTypeAvatarName({
      type,
      label,
      isAssessmentTypeConfigurationEnabled,
    });

    return {
      taskTypeId,
      label: label,
      key: taskTypeId,
      value: taskTypeId,
      taskType: type,
      templateId,
      icon: (
        <Avatar
          shape="square"
          size="large"
          name={avatarName}
          color={color}
          dsVersion="2.0"
        />
      ),
      disabled: !isTermsExistsForCurrentCurriculumProgram,
      disabledTooltip: t(
        "classRoom:assessment_creation_no_grading_periods_tooltip_with_label_plural",
        { label }
      ),
    };
  });

  return [
    {
      label: "common:create",
      key: "CREATE",
      isItemGroup: true,
      options: [
        ...getDepthWiseSectionOptions({ depth, isFolder }),
        ...(hideAssessments ? [] : assessmentOptions),
        {
          label: "common:note",
          key: "ADD_NOTE",
          icon: <NoteOutlined />,
        },
        {
          label: "common:link",
          key: "ADD_LINK",
          icon: <LinkOutlined />,
        },
        {
          label: "common:upload_from",
          key: "upload_menu",
          isSubMenu: true,
          showDivider: false,
          icon: <UploadOutlined />,
          options: [
            {
              key: "add_google_drive",
              label: "common:google_drive",
              icon: <GoogleDriveColored />,
            },
            {
              key: "add_one_drive",
              label: "common:one_drive",
              icon: <OneDriveColored />,
            },
            {
              key: "upload_device",
              label: "common:device",
              icon: <DeviceDesktopOutlined />,
            },
          ],
        },
        {
          key: "MORE",
          label: "common:more",
          icon: <DotsHorizontalOutlined />,
          isSubMenu: true,
          options: [],
        },
      ],
    },
    {
      label: "common:import_from",
      key: "import_menu",
      isSubMenu: true,
      icon: <ImportOutlined />,
      options: [
        ...(hideAssessments
          ? []
          : [
              {
                key: "IMPORT_UNIT_FLOW",
                label: "common:unit",
                icon: <ListDashedSlantedOutlined />,
                disabled: !isTermsExistsForCurrentCurriculumProgram,
                disabledTooltip: t(
                  "classRoom:import_from_label_no_grading_periods_tooltip",
                  {
                    label: t("common:unit"),
                  }
                ),
              },
            ]),
        {
          key: "IMPORT_LE_FROM_LE_LIBRARY",
          label: "common:learning_experience_and_assessment_library",
          icon: <LeLibraryOutlined />,
          disabled: !isTermsExistsForCurrentCurriculumProgram,
          disabledTooltip: t(
            "classRoom:import_from_label_no_grading_periods_tooltip",
            {
              label: t("common:learning_experience_and_assessment_library"),
            }
          ),
        },
        ...(isMapleBearLikeOrg || hideAssessments || !canImportFromToddleLibrary
          ? []
          : [
              {
                key: "IMPORT_FROM_TODDLE_LIBRARY",
                label: "common:toddle_library",
                icon: <JournalOutlined />,
                disabled: !isTermsExistsForCurrentCurriculumProgram,
                disabledTooltip: t(
                  "classRoom:import_from_label_no_grading_periods_tooltip",
                  {
                    label: t("common:toddle_library"),
                  }
                ),
              },
            ]),
        {
          key: "IMPORT_FROM_ANOTHER_CLASS",
          label: "classMaterials:another_class_or_academic_year",
          icon: <ClassProgressOutlined />,
          disabled: !isTermsExistsForCurrentCurriculumProgram,
          disabledTooltip: t(
            "classRoom:import_from_label_no_grading_periods_tooltip",
            {
              label: t("common:toddle_library"),
            }
          ),
        },
      ],
    },
  ];
};

export const getAddDropDownOptionsMemoized = getMemoizedLangFunction(
  getAddDropDownOptions
);

export const getAddDropDownOptionsV2Memoized = getMemoizedLangFunction(
  getAddDropDownOptionsV2
);

const getMoveToSectionOption = ({
  t,
  itemType,
  itemDepth,
  maxChildDepth = 0,
  sectionsCount,
}) => {
  // no need to show move to section option if there are
  // no sections in the feed
  if (sectionsCount === 0) return [];

  const isItemFolder = itemType === "FOLDER";
  if (isItemFolder && itemDepth > MAX_FOLDER_DEPTH) return [];

  let itemStructureDepth = 1;

  if (maxChildDepth > 0) {
    itemStructureDepth += maxChildDepth;
    itemStructureDepth -= itemDepth;
  }

  if (isItemFolder) {
    itemStructureDepth += 1;
  }

  const moveToSectionLabel =
    itemDepth === 1
      ? t("common:move_to_section")
      : t("common:move_to_another_section");

  return [
    {
      key: "MOVE_TO_SECTION",
      label: moveToSectionLabel,
      icon: <SwitchArrowsOutlined />,
      disabled: itemStructureDepth > MAX_FOLDER_DEPTH,
      disabledTooltip: t(
        "common:hierarchy_level_restriction_for_section_with_level",
        { level: MAX_DEPTH }
      ),
    },
  ];
};

const getCardDropdownOptions = ({
  attachmentType,
  type,
  curriculumProgramType,
  isShared,
  itemDepth,
  sectionsCount,
  maxChildDepth,
  isExpanded,
  showExpandCollapseAllOption,
  hasLockedAcademicTermChildren = false,
  t,
}) => {
  const moveToSectionOption = getMoveToSectionOption({
    t,
    itemType: type,
    itemDepth,
    maxChildDepth,
    sectionsCount,
  });

  switch (type) {
    case "FOLDER": {
      return [
        ...(isShared
          ? [
              {
                key: "SECTION_UNSHARE",
                label: t("classMaterials:unshare_section_only"),
                icon: <UserCloseOutlined />,
              },
            ]
          : []),
        {
          key: "SECTION_RENAME",
          label: t("classMaterials:edit_section_title"),
          icon: <PencilLineOutlined />,
        },
        isExpanded
          ? {
              key: "COLLAPSE_ALL_SECTIONS",
              label: showExpandCollapseAllOption
                ? t("classMaterials:collapse_all_sections")
                : t("common:collapse"),
              icon: <CaretOpenedFilled />,
            }
          : {
              key: "EXPAND_ALL_SECTIONS",
              label: showExpandCollapseAllOption
                ? t("classMaterials:expand_all_sections")
                : t("common:expand"),
              icon: <CaretClosedFilled />,
            },
        ...moveToSectionOption,
        {
          key: "SECTION_DELETE",
          label: t("common:delete"),
          icon: <DeleteOutlined />,
          disabled: hasLockedAcademicTermChildren,
          disabledTooltip: t(
            "course:you_cannot_delete_parent_since_it_has_locked_items",
            {
              parentEntityLabel: t("common:folder"),
            }
          ),
          isDestructive: true,
        },
      ];
    }

    case "FILE": {
      const isLink = attachmentType === "LINK";
      const isNote = attachmentType === "NOTE";

      return [
        ...(isShared
          ? [
              {
                key: "FILE_UNSHARE",
                label: t("classMaterials:unsahre_resource"),
                icon: <UserCloseOutlined />,
              },
            ]
          : []),
        ...(isNote
          ? [
              {
                key: "NOTE_EDIT",
                label: t("common:edit"),
                icon: <PencilLineOutlined />,
              },
            ]
          : []),
        ...(isLink
          ? [
              {
                key: "EDIT_LINK",
                label: t("common:edit"),
                icon: <PencilLineOutlined />,
              },
            ]
          : []),
        ...(!isLink && !isNote
          ? [
              {
                key: "FILE_RENAME",
                label: t("common:rename"),
                icon: <PencilLineOutlined />,
              },
            ]
          : []),
        {
          key: "FILE_VIEW",
          label: t("common:view"),
          icon: <EyeOutlined />,
        },
        ...moveToSectionOption,
        ...(!isLink && !isNote
          ? [
              {
                key: "FILE_DOWNLOAD",
                label: t("common:download"),
                icon: <DownloadOutlined />,
              },
              {
                key: "DELETE_DIVIDER",
                isDivider: true,
              },
            ]
          : []),
        {
          key: "FILE_DELETE",
          label: t("common:delete"),
          icon: <DeleteOutlined />,
          isDestructive: true,
        },
      ];
    }

    case "ASSIGNMENT": {
      return [
        {
          key: "ASSIGNMENT_EDIT",
          label: t("common:edit"),
          icon: <PencilLineOutlined />,
        },
        {
          key: "ASSIGNMENT_VIEW",
          label: t("common:view"),
          icon: <EyeOutlined />,
        },
        ...(curriculumProgramType === CURRICULUM_TYPE_PYP
          ? [
              {
                key: "ASSIGNMENT_TAG_SNP",
                label: t("common:tag_snp"),
                icon: <TagOutlined />,
              },
            ]
          : []),
        {
          key: "DIVIDER_1",
          isDivider: true,
        },
        {
          key: "ASSIGNMENT_MARK_EVALUATED",
          label: t("classMaterials:mark_all_as_evaluated"),
          icon: <StudentEvaluateOutlined />,
        },
        {
          key: "DIVIDER_2",
          isDivider: true,
        },
        {
          key: "ASSIGNMENT_REMOVE",
          label: t("classMaterials:remove_from_class_materials"),
          icon: <MinusCircleOutlined />,
          isDestructive: true,
        },
        {
          key: "ASSIGNMENT_DELETE",
          label: t("common:delete"),
          icon: <DeleteOutlined />,
          isDestructive: true,
        },
      ];
    }
  }

  return [];
};

export const getCardDropdownOptionsMemoized = getMemoizedLangFunction(
  getCardDropdownOptions
);

const getImportFromAnotherClassModalSteps = ({ t }) => {
  return [
    {
      value: "SELECT_CLASS",
      label: t("toddleLibrary:select_class"),
      config: {
        heading: t("toddleLibrary:select_class"),
        footerConfig: {
          fullWidth: true,
          actionButtons: [
            {
              label: "common:cancel",
              value: "cancel",
            },
            {
              label: "common:next",
              value: "next",
            },
          ],
        },
      },
    },
    {
      value: "SELECT_ITEMS",
      label: t("common:select_items"),
      config: {
        heading: t("common:select_items"),
        isMultiSelect: true,
        footerConfig: {
          fullWidth: true,
          actionButtons: [
            {
              label: "common:previous",
              value: "cancel",
            },
            {
              label: "classMaterials:import_as_drafts",
              value: "import",
            },
          ],
        },
      },
    },
  ];
};

export const getImportFromAnotherClassModalStepsMemoized = getMemoizedLangFunction(
  getImportFromAnotherClassModalSteps
);

export const getIsProSchool = () => {
  const state = store.getState();
  const curriculumPrograms = _.get(
    state,
    "platform.organizationCurriculumPrograms",
    []
  );

  return _.every(curriculumPrograms, curriculumProgram =>
    _.includes(
      [PLAN_TYPE_PRO, PLAN_TYPE_PRO_PILOT],
      _.get(curriculumProgram, "subscriptionPlan.type")
    )
  );
};

const getCardActions = ({
  t,
  type,
  isShared,
  students,
  assessmentlabel,
  isGradingPeriodUnlocked = true,
  totalChildren = 0,
  curriculumProgramType,
  courseSubjectLevels,
  isPreviousAcademicYearSelected = false,
}) => {
  if (isPreviousAcademicYearSelected) {
    return [];
  }

  const hasStudents = _.size(students) > 0;
  const isProSchool = getIsProSchool();

  switch (type) {
    case "FOLDER": {
      if (!isShared) {
        return [
          totalChildren > 0
            ? {
                key: "SHARE_BUTTON",
                type: "SplitButton",
                action: "SECTION_SHARE_ALL",
                isDisabled: !hasStudents,
                label: t("classMaterials:share_section_with_all_items"),
                ...(!hasStudents
                  ? { tooltip: t("classMaterials:no_student_section_share") }
                  : {}),
                icon: <MarkAsReadOutlined />,
                options: [
                  {
                    key: "SECTION_SHARE_ALL",
                    label: t("classMaterials:share_section_with_all_items"),
                    subText: t(
                      "classMaterials:share_section_with_all_items_subtext"
                    ),
                    icon: <MarkAsReadOutlined />,
                  },

                  {
                    key: "SECTION_SHARE",
                    label: t("classMaterials:share_section_only"),
                    subText: t("classMaterials:share_section_only_subtext"),
                    icon: <TickCircleOutlined />,
                  },
                ],
              }
            : {
                key: "SECTION_SHARE_ALL",
                type: "Button",
                action: "SECTION_SHARE_ALL",
                label: t("classMaterials:share_section"),
                isDisabled: !hasStudents,
                icon: <MarkAsReadOutlined />,
                ...(!hasStudents
                  ? { tooltip: t("classMaterials:no_student_section_share") }
                  : {}),
              },
        ];
      }
      break;
    }

    case "ASSIGNMENT": {
      if (!isShared && !isProSchool) {
        const isDP = _.isEqual(CURRICULUM_TYPE_DP, curriculumProgramType);

        const hasMultipleSubjectLevels = _.size(courseSubjectLevels) > 1;

        return [
          {
            key: "QUICK_ASSIGN",
            type: "Button",
            action: "ASSIGNMENT_QUICK_ASSIGN",
            label: t("classMaterials:quick_assign"),
            isDisabled: !hasStudents || !isGradingPeriodUnlocked,
            icon: <TickCircleOpenOutlined />,
            tooltip: !isGradingPeriodUnlocked
              ? t("course:you_cannot_with_label_since_locked", {
                  actionLabel: t("common:assign"),
                })
              : hasStudents
              ? isDP && hasMultipleSubjectLevels
                ? t("classMaterials:assign_to_all_with_default_settings_dp")
                : t("classMaterials:assign_to_all_with_default_settings")
              : t("classMaterials:no_student_quick_assign", {
                  label: assessmentlabel,
                }),
          },
          {
            key: "ASSIGN",
            type: "Button",
            action: "ASSIGNMENT_ASSIGN",
            label: t("common:assign"),
            isDisabled: !isGradingPeriodUnlocked,
            icon: <UserTickOutlined />,
            tooltip: !isGradingPeriodUnlocked
              ? t("course:you_cannot_with_label_since_locked", {
                  actionLabel: t("common:assign"),
                })
              : t("classMaterials:select_classes_to_share_and_customise"),
          },
        ];
      }
      break;
    }

    case "FILE": {
      if (!isShared) {
        return [
          {
            key: "QUICK_SHARE",
            type: "Button",
            action: "FILE_QUICK_SHARE",
            label: t("classMaterials:quick_share"),
            isDisabled: !hasStudents,
            icon: <TickCircleOpenOutlined />,
            tooltip: hasStudents
              ? t("classMaterials:shares_with_all_students")
              : t("classMaterials:no_student_quick_share"),
          },
          {
            key: "ASSIGN",
            type: "Button",
            action: "FILE_SHARE",
            label: t("common:share"),
            icon: <UserTickOutlined />,
            tooltip: t("classMaterials:share_with_individual"),
          },
        ];
      }
      break;
    }
  }
};

export const getCardActionsMemoized = getMemoizedLangFunction(
  getCardActions,
  ({ students, ...restParams }) =>
    JSON.stringify({
      ...restParams,
      students: _.map(students, "id"),
    })
);

const getDisplaySettings = ({
  t,
  isUserTeacher,
  showExpandCollapseAllOption,
}) => {
  return [
    {
      key: "show_outline",
      label: t("classMaterials:show_outline"),
      type: "checkbox",
      hasDivider: true,
    },
    ...(showExpandCollapseAllOption
      ? [
          {
            key: "collapse_sections",
            isItemGroup: true,
            options: [
              {
                key: "collapse_all_sections",
                label: t("classMaterials:collapse_all_sections"),
                type: "radio",
              },
              {
                key: "expand_all_sections",
                label: t("classMaterials:expand_all_sections"),
                type: "radio",
              },
            ],
          },
        ]
      : []),
    ...(isUserTeacher
      ? [
          {
            key: "default_item_placement",
            label: t("classMaterials:default_item_placement"),
            isItemGroup: true,
            options: [
              {
                key: "add_to_top",
                label: t("classMaterials:add_to_top"),
                type: "radio",
              },
              {
                key: "add_to_bottom",
                label: t("classMaterials:add_to_bottom"),
                type: "radio",
              },
            ],
          },
        ]
      : []),
    {
      key: "task_resource_icon",
      label: t("classMaterials:task_resource_icon"),
      isItemGroup: true,
      options: [
        {
          key: "thumbnail_image",
          label: t("classMaterials:thumbnail_image"),
          type: "radio",
        },
        {
          key: "task_resource_type",
          label: t("classMaterials:task_resource_type"),
          type: "radio",
        },
      ],
    },
    {
      key: "task_details",
      label: t("classMaterials:task_details"),
      isItemGroup: true,
      options: [
        {
          key: "assessment_category",
          label: t("classMaterials:assessment_category"),
          type: "checkbox",
        },
        {
          key: "due_or_scheduled_date",
          label: t("classMaterials:due_or_scheduled_date"),
          type: "checkbox",
        },
        {
          key: "locked_status",
          label: t("classMaterials:locked_status"),
          type: "checkbox",
        },
        {
          key: "task_status",
          label: t("classMaterials:task_status"),
          type: "checkbox",
        },
      ],
    },
  ];
};

export const getDisplaySettingsMemoized = getMemoizedLangFunction(
  getDisplaySettings
);

const getClassMaterialsFeedVariables = ({
  courseId,
  userInfo,
  userType,
  filters: filtersInState,
  curriculumType,
}) => {
  let studentFilters = {};
  const isUserParent = isUserTypeParent({ userType });
  const isUserStudent = isUserTypeStudent({ userType });
  const isAssessmentTypeConfigurationEnabled = getIsAssessmentTypeConfigurationEnabled();

  const classMaterialFilters = _.mapValues(filtersInState, (value, key) => {
    const isAllOption = _.isEqual(value, "ALL") && key != "searchText";
    return isAllOption ? [] : value;
  });

  const isFlatFeedFilter = _.some(
    classMaterialFilters,
    value => !_.isEmpty(value)
  );
  const {
    selectedItemTypes = [],
    selectedCategories = [],
    selectedStudents = [],
    selectedAddedBy = [],
    selectedStudentSubmissionStatus = [],
    searchText = "",
    selectedTaskTypeIds = [],
  } = classMaterialFilters;

  const createdBy = selectedAddedBy == "ME" ? userInfo?.id : [];

  const {
    selectedAssessmentTypes,
    selectedResourceTypes,
  } = splitAssessmentAndResourceTypesMemoized({ selectedItemTypes });

  if (userInfo) {
    if (isUserParent) {
      const childId = userInfo.childID;
      studentFilters = {
        studentIds: [childId],
      };
    } else if (isUserStudent) {
      studentFilters = {
        studentIds: [userInfo.id],
      };
    }
  }

  let assessmentFilters = {};

  // Add taskTypeIds when FF is enabled
  const taskTypeIds = isAssessmentTypeConfigurationEnabled
    ? selectedTaskTypeIds
    : undefined;

  if (isAssessmentTypeConfigurationEnabled) {
    // When FF is on, only include category and student status and taskTypeId filters
    assessmentFilters = {
      taskTypeIds,
      assessmentCategory: selectedCategories,
      studentAssignmentStatus: selectedStudentSubmissionStatus,
    };
  } else {
    // When FF is off, use existing logic with assessmentTypes and groupTypes
    const { assessmentTypes = [], groupTypes = [] } = getGroupOrAssessmentTypes(
      {
        curriculumType,
        selectedAssessmentTypes,
      }
    );

    assessmentFilters = {
      assessmentType: assessmentTypes,
      groupTypes,
      assessmentCategory: selectedCategories,
      studentAssignmentStatus: selectedStudentSubmissionStatus,
    };
  }

  const filters = {
    types: ["CLASS_MATERIAL"],
    resourceTypes: ["FOLDER", "FILE", "ASSIGNMENT"],
    isFlatFeedFilter,
    assessmentFilters,
    itemTypes: selectedResourceTypes,
    studentIds: selectedStudents,
    searchText,
    createdBy,
    ...(isUserParent ? studentFilters : {}),
  };

  return {
    parentId: courseId,
    parentType: "COURSE",
    filters,
    orderBy: "DISPLAY_SEQUENCE",
    unreadMessageCountFilters: {
      includeItemTypesV2: INCLUDE_ITEM_TYPES_FOR_UNREAD_MESSAGES_COUNT,
    },
    first: canFetchLargeClassFlowData() ? 100 : 20,
    ...studentFilters,
    // we need to fetch assigned students when on parent or student
    // because submission status is inside this
    skipAssignedStudents: !(isUserParent || isUserStudent),
  };
};

export const getClassMaterialsFeedVariablesMemoized = _.memoize(
  params => getClassMaterialsFeedVariables(params),
  ({ userInfo, ...restParams }) =>
    JSON.stringify({
      ...restParams,
      ...(userInfo
        ? {
            userInfo: {
              id: userInfo.id,
              childID: userInfo.childID,
            },
          }
        : {}),
    })
);

const getClassMaterialsSyncStatusVariables = ({ orgId, courseId }) => {
  return {
    id: orgId,
    courseIds: [courseId],
    type: ["CLASS_MATERIAL_BULK_IMPORT"],
  };
};

export const getClassMaterialsSyncStatusVariablesMemoized = _.memoize(
  params => getClassMaterialsSyncStatusVariables(params),
  ({ orgId, courseId }) => JSON.stringify({ orgId, courseId })
);

const getPointSetVariables = ({
  organizationId,
  curriculumProgramId,
  courseId,
  gradeId,
}) => {
  const associatedEntities = [];

  if (gradeId) {
    associatedEntities.push({
      type: "GRADE",
      ids: gradeId,
    });
  }

  const isCourseApplicableToCategorySet =
    checkCategorySetApplicableTo({
      organizationId,
    }) === "COURSE";

  if (isCourseApplicableToCategorySet && courseId) {
    associatedEntities.push({
      type: "COURSE",
      ids: [courseId],
    });
  }

  return {
    id: organizationId,
    filters: {
      curriculumProgramIds: curriculumProgramId ? [curriculumProgramId] : null,
      // "associatedEntites" is misspelled in query filters
      associatedEntites: associatedEntities,
      types: "SCORE",
    },
  };
};

export const getPointSetVariablesMemoized = getMemoizedFunction(
  getPointSetVariables
);

const _getOutlineFeedQueryVariables = ({
  courseId,
  unitId,
  parentType,
  isImportFlow = false,
  filters,
  userInfo,
  userType,
  curriculumType,
}) => {
  let studentFilters = {};
  const isUserParent = isUserTypeParent({ userType });
  const isUserStudent = isUserTypeStudent({ userType });
  const isAssessmentTypeConfigurationEnabled = getIsAssessmentTypeConfigurationEnabled();

  const classMaterialFilters = _.mapValues(filters, (value, key) => {
    const isAllOption = _.isEqual(value, "ALL") && key != "searchText";
    return isAllOption ? [] : value;
  });

  const isFlatFeedFilter = _.some(
    classMaterialFilters,
    value => !_.isEmpty(value)
  );
  const {
    selectedItemTypes = [],
    selectedCategories = [],
    selectedStudents = [],
    selectedAddedBy = [],
    selectedStudentSubmissionStatus = [],
    searchText = "",
    selectedTaskTypeIds = [],
  } = classMaterialFilters;

  const createdBy = selectedAddedBy == "ME" ? userInfo?.id : [];

  const {
    selectedAssessmentTypes,
    selectedResourceTypes,
  } = splitAssessmentAndResourceTypesMemoized({ selectedItemTypes });

  if (userInfo) {
    if (isUserParent) {
      const childId = userInfo.childID;
      studentFilters = {
        studentIds: [childId],
      };
    } else if (isUserStudent) {
      studentFilters = {
        studentIds: [userInfo.id],
      };
    }
  }

  let assessmentFilters = {};

  if (isAssessmentTypeConfigurationEnabled) {
    // When FF is on, only include category and student status filters
    assessmentFilters = {
      assessmentCategory: selectedCategories,
      studentAssignmentStatus: selectedStudentSubmissionStatus,
    };
  } else {
    // When FF is off, use existing logic with assessmentTypes and groupTypes
    const { assessmentTypes = [], groupTypes = [] } = getGroupOrAssessmentTypes(
      {
        curriculumType,
        selectedAssessmentTypes,
      }
    );

    assessmentFilters = {
      assessmentType: assessmentTypes,
      groupTypes,
      assessmentCategory: selectedCategories,
      studentAssignmentStatus: selectedStudentSubmissionStatus,
    };
  }

  // Add taskTypeIds when FF is enabled
  const taskTypeIds = isAssessmentTypeConfigurationEnabled
    ? selectedTaskTypeIds
    : undefined;

  return {
    parentId: isImportFlow ? unitId : courseId,
    parentType: isImportFlow ? parentType : COURSE_PARENT_TYPE,
    filters: {
      types: isImportFlow ? [PLANNER_FLOW_RESOURCE] : [CLASS_MATERIAL_TYPE],
      resourceTypes: [CLASS_MATERIAL_RESOURCE_TYPE],
      maxDepth: MAX_FOLDER_DEPTH,
      isFlatFeedFilter,
      assessmentFilters,
      itemTypes: selectedResourceTypes,
      studentIds: selectedStudents,
      searchText,
      createdBy,
      ...(isUserParent ? studentFilters : {}),
    },
    orderBy: CLASS_MATERIAL_ORDER_BY,
    ...studentFilters,
    ...(isAssessmentTypeConfigurationEnabled && !_.isEmpty(taskTypeIds)
      ? { taskTypeIds }
      : {}),
  };
};

export const getOutlineFeedQueryVariables = _.memoize(
  params => _getOutlineFeedQueryVariables(params),
  ({ userInfo, ...restParams }) =>
    JSON.stringify({
      ...restParams,
      ...(userInfo
        ? {
            userInfo: {
              id: userInfo.id,
              childID: userInfo.childID,
            },
          }
        : {}),
    })
);

export const getUnreadNotificationCount = ({
  resource,
  items = [],
  isItemFolder = false,
  collapsed = false,
}) => {
  let count = 0;

  const { resourceType, item } = resource;

  if (isItemFolder && collapsed) {
    const children = getNestedChildren({ items, id: resource.id });

    count = _.reduce(
      children,
      (result, child) => {
        if (child.resourceType !== "ASSIGNMENT") return result;
        return (
          result +
          getUnreadNotificationCount({
            resource: child,
          })
        );
      },
      0
    );
  } else if (resourceType === "ASSIGNMENT" && item) {
    count += _.get(item, "summaryCounts.unreadMessagesCount", 0);
  }

  return count;
};

export const getClassFlowStudentsVariables = ({ itemId = null }) => ({
  studentIds: [],
  unreadMessageCountFilters: {
    includeItemTypesV2: INCLUDE_ITEM_TYPES_FOR_UNREAD_MESSAGES_COUNT,
  },
  ids: itemId ? [itemId] : [],
});

const getLinkedResourcesVariables = ({ id = null }) => ({
  ids: [id],
});

export const getLinkedResourcesVariablesMemoized = _.memoize(
  params => getLinkedResourcesVariables(params),
  ({ id }) => JSON.stringify({ id })
);

export const getSelectedFilterValues = ({
  filters,
  itemTypeOptions,
  categoryOptions,
  students,
  studentStatusFilterOptions,
}) => {
  const {
    selectedItemTypes,
    selectedCategories,
    selectedStudents,
    selectedStudentSubmissionStatus,
    selectedResourceTypes,
  } = filters;

  return {
    selectedItemTypes:
      selectedItemTypes == "ALL"
        ? _.map(itemTypeOptions, "value")
        : selectedItemTypes,
    selectedCategories:
      selectedCategories == "ALL"
        ? _.map(categoryOptions, "value")
        : selectedCategories,
    selectedStudents:
      selectedStudents == "ALL" ? _.map(students, "id") : selectedStudents,
    selectedStudentSubmissionStatus:
      selectedStudentSubmissionStatus == "ALL"
        ? _.map(studentStatusFilterOptions, "value")
        : selectedStudentSubmissionStatus,
    selectedResourceTypes: selectedResourceTypes == "ALL",
  };
};

const getStudentStatusFilterOptions = ({ t }) => {
  const filteredOptions = [
    {
      value: "TODO",
      label: t("classRoom:todo"),
      options: [
        ...PUBLISHED_STUDENT_ASSIGNMENT_STATUS,
        ...PUBLISHED_STUDENT_ASSIGNMENT_LOCKING_SUBMISSION_STATUS,
        {
          ...ASSIGNMENT_STATE_TABS.SCHEDULED,
          label: ASSIGNMENT_STATE_TABS.SCHEDULED.studentLabel,
        },
      ],
    },
    {
      value: "DONE",
      label: t("common:done"),
      options: [...getArchivedStudentAssignmentStatus()],
    },
  ];

  _.forOwn(filteredOptions, value => {
    value.options = _.map(value.options, ({ label, value }) => ({
      key: value,
      value,
      label: t(label),
    }));
  });

  return filteredOptions;
};

export const getStudentStatusFilterOptionsMemoized = getMemoizedLangFunction(
  getStudentStatusFilterOptions
);

const getAddedByOptions = ({ t }) => {
  return [
    {
      key: "ALL",
      label: t("common:all"),
      value: "ALL",
    },
    {
      key: "ME",
      label: t("common:me"),
      value: "ME",
    },
  ];
};

export const getAddedByOptionsMemoized = getMemoizedLangFunction(
  getAddedByOptions
);

export const getClassFlowImportFilters = ({ curriculumType, filters }) => {
  const searchText = _.get(filters, "searchText", "");
  const selectedItemTypes = _.get(filters, "selectedItemTypes", "ALL");
  const {
    selectedAssessmentTypes,
    selectedResourceTypes,
  } = splitAssessmentAndResourceTypesMemoized({ selectedItemTypes });

  const { assessmentTypes = [], groupTypes = [] } = getGroupOrAssessmentTypes({
    curriculumType,
    selectedAssessmentTypes,
  });

  const assessmentFilters = {
    assessmentType: assessmentTypes,
    groupTypes,
  };

  const isContentTypeFilterApplied =
    !_.isEmpty(selectedAssessmentTypes) || !_.isEmpty(selectedResourceTypes);

  const queryFilters = {
    types: ["CLASS_MATERIAL"],
    assessmentFilters,
    resourceTypes: ["FOLDER", "FILE", "ASSIGNMENT"],
    itemTypes: selectedResourceTypes,
    isFlatFeedFilter: isContentTypeFilterApplied,
    searchText,
  };

  return queryFilters;
};

export const getClassFlowImportVariables = ({ selectedClassId, filters }) => {
  return {
    parentId: selectedClassId,
    parentType: "COURSE",
    orderBy: "DISPLAY_SEQUENCE",
    filters,
    first: canFetchLargeClassFlowData() ? 100 : 20,
    skipAssignedStudents: true,
  };
};

export const getToastMsgForResourceUpdate = resourceType => {
  let msg = "";
  switch (resourceType) {
    case "LINK": {
      msg = "classMaterials:toast_msg_for_link_update";
      break;
    }
    case "NOTE": {
      msg = "classMaterials:toast_msg_for_note_update";
      break;
    }
    case "FILE": {
      msg = "classMaterials:toast_msg_for_resource_rename";
      break;
    }
    default: {
      msg = "classMaterials:toast_msg_for_section_rename";
      break;
    }
  }

  return {
    msg,
    type: "success",
    position: "toast-bottom-left",
  };
};

export const canFetchLargeClassFlowData = () =>
  ACLStore.can("FeatureFlag:ClassFlowLargeDataFetch");

export const getResourceData = ({ item, variables }) => {
  const { id, depth, parent, label, resourceType } = item;
  const childFeedData = getClassMaterialItemFromCache({ id, variables });

  if (!_.isEmpty(childFeedData)) {
    return { ...childFeedData, depth };
  }

  return {
    id,
    label,
    displaySequence: 1,
    depth,
    resourceType,
    isSkeleton: true,
    state: "DRAFT",
    attachment: null,
    linkedResources: [],
    __typename: "OrganizationResource",
    item: null,
    parent,
    childrenHierarchyInfo: null,
    students: [],
    childrenV2: {
      edges: [],
      __typename: "OrganizationResourceConnection",
    },
    isNew: false,
  };
};

export const getIsAssignmentBackdated = item => {
  const { item: assignmentItem = {} } = item || {};
  const { state = {}, deadline = "" } = assignmentItem;
  const { publishedAt = "" } = state;

  // Return false if either date is missing
  if (_.isEmpty(publishedAt) || _.isEmpty(deadline)) {
    return false;
  }

  const currentTime = moment();

  // Check if both dates are in the past
  const isPublishedInPast = checkIsDateBefore({
    date1: publishedAt,
    date2: currentTime,
    key: "minutes",
  });
  const isDeadlineInPast = checkIsDateBefore({
    date1: deadline,
    date2: currentTime,
    key: "minutes",
  });

  return isPublishedInPast && isDeadlineInPast;
};

export const getHasLockedAcademicTermChildren = ({
  item,
  resourceRelationConfig,
}) => {
  const itemId = _.get(item, "id", null);
  if (!itemId) return false;

  const childrenId = _.get(
    resourceRelationConfig,
    `childrenConfig.${itemId}`,
    []
  );
  const resourcesConfig = _.get(resourceRelationConfig, "resourcesConfig", {});
  return _.some(childrenId, childId => {
    const childItem = _.get(resourcesConfig, childId, {});
    const academicTerm = _.get(childItem, "item.content.academicTerm");
    const assessmentType = _.get(
      childItem,
      "item.content.assessmentType.value"
    );
    return !getIsGradingPeriodUnlocked(academicTerm, assessmentType);
  });
};

export const getIsRealTimeEnabled = () => {
  return ACLStore.can("FeatureFlag:EnableClassFlowRealtimeUpdates");
};
